import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash-es';

import { ReactComponent as SuccessIcon } from '../../../../assets/images/successIcon.svg';
import Button from '../../../../components/shared/Button/Button';
import ProgressBar from '../../../../components/shared/ProgressBar/ProgressBar';
import { PROGRESS_QUESTIONS, PROGRESS_VALUES } from '../../../../constants/strengthenQuestionsConstants';
import { candidateQuestionsSuccess, userFilesSuccess } from '../../../../helpers/candidateQuestionHelper';

import styles from './CandidateQuestionsSuccess.module.scss';

const CandidateQuestionsSuccess = ({ data, onClick = () => {} }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const progressBarPercent = data.organization_name === 'Connect Pediatrics' ? Math.ceil(80 / 9) : 80 / 8;
  const { skills, questions, resume_file_links, links_json, job_uuid, status } = data;
  const currentUser = useSelector((state) => state.authReducer.currentUser);
  const authenticated = !!currentUser;
  const candidateQuestions = useMemo(() => candidateQuestionsSuccess(data, styles, t), [data, t]);
  const userFiles = useMemo(() => userFilesSuccess(data, styles), [data]);

  const progress = useMemo(() => {
    let progressPercentage = 20;
    PROGRESS_VALUES.forEach((value) => {
      if (value === 'skills' && !isEmpty(skills)) {
        progressPercentage += progressBarPercent;
      } else if (value === 'resume_file_links' && !isEmpty(resume_file_links)) {
        progressPercentage += progressBarPercent;
      } else if (value === 'links_json' && !isEmpty(links_json)) {
        progressPercentage += progressBarPercent;
      } else if (value === 'diversity_hire' && questions[value]) {
        progressPercentage += progressBarPercent;
      } else if (!isEmpty(questions[value])) {
        progressPercentage += progressBarPercent;
      }
    });
    const totalProgress = Math.min(progressPercentage, 100);
    return totalProgress;
  }, [data]);

  const userLinks = useMemo(() => links_json.map(({ value }) => value).join(', '), [data]);

  const handleViewTracker = useCallback(() => {
    if (authenticated) {
      navigate(`/tracker/details?jobId=${job_uuid}&direction=my`);
    } else {
      navigate('/signin');
    }
  }, [authenticated, navigate, job_uuid]);

  return (
    <div className={styles.successWrapper}>
      <div className={styles.successContainer}>
        <div className={styles.whiteContainer}>
          <div className={styles.strengthContainer}>
            <div>
              <ProgressBar heading={t('application.applicationStrength')} percentage={progress} />
            </div>
            <div>
              {!isEmpty(resume_file_links) && (
                <div className={styles.questionContainer}>
                  <span className={styles.question}>{t(PROGRESS_QUESTIONS.files)} &nbsp;</span>
                  {userFiles}
                </div>
              )}

              {!isEmpty(links_json) && (
                <div className={styles.questionContainer}>
                  <span className={styles.question}>{t(PROGRESS_QUESTIONS.links_json)} &nbsp;</span>
                  {userLinks}
                </div>
              )}

              {candidateQuestions}

              <div>
                {status !== 'archived' && status !== 'hired' && progress !== 100 && (
                  <div className={styles.questionContainer}>
                    <div className={styles.questionContainer}>
                      <button onClick={onClick} type="button" className={styles.button}>
                        {t('candidateQuestions.getStrengthTo100')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.successIconContainer}>
              <SuccessIcon />
              <p className={styles.heading}>{t('success')}</p>
              <p className={styles.text}>{t('application.completedSuccessfully')}</p>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <Button onClick={handleViewTracker}>{t('buttons.viewInTracker')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateQuestionsSuccess;
